// A table used by every list in Stage 2: search, filters, sorting, choose columns, select rows for bulk
// changes, saved filters (per person) and Excel export. Styled with the v2.0.41 panel/table look.
import { confirmBox, del, esc, get, post, toast } from "./lib.js";
import { navigate } from "./state.js";

export interface Column<T> {
  key: string;
  label: string;
  /** Plain value used for sorting, searching and filters. */
  value: (row: T) => unknown;
  /** Cell HTML (defaults to the escaped value). */
  html?: (row: T) => string;
  /** Start hidden (people can switch it on under Columns). */
  hidden?: boolean;
  className?: string;
  numeric?: boolean;
}
/** A drop-down filter, or with `toggle` a tick box whose test always runs (value "1" when ticked, "" when not). */
export interface Filter<T> { key: string; label: string; options: Array<[string, string]>; test: (row: T, value: string) => boolean; initial?: string; toggle?: boolean }
export interface BulkAction { id: string; label: string; danger?: boolean; confirm?: string; choose?: { label: string; options: Array<[string, string]> } }

export interface TableOptions<T> {
  page: string;                        // key for saved filters and column choices
  rows: T[];
  columns: Column<T>[];
  rowId: (row: T) => string;
  rowHref?: (row: T) => string;
  rowClass?: (row: T) => string;
  filters?: Filter<T>[];
  bulkActions?: BulkAction[];
  onBulk?: (action: string, ids: string[], value?: string) => Promise<void>;
  /** A download of these rows (null = all of them) as Excel or CSV; the buttons export what is shown (search and filters). */
  exportUrl?: (ids: string[] | null, fmt?: "xlsx" | "csv") => string;
  /** More downloads next to Export (e.g. the SKUs of the garments shown). */
  exportExtra?: Array<{ label: string; url: (ids: string[] | null) => string }>;
  searchPlaceholder?: string;
  /** Text already in the search box when the table opens (e.g. from an Action Centre link). */
  initialSearch?: string;
  emptyText?: string;
  title?: string;
  subtitle?: string;
  headActions?: string;                // extra buttons in the panel head
  /** A summary bar above the list, for the rows currently shown (after search and filters). */
  summary?: (shown: T[]) => string;
  /** A totals row at the bottom, for the rows currently shown: cell HTML by column key (the first column shows "Total"). */
  totals?: (shown: T[]) => Record<string, string>;
}

interface TableState { search: string; filters: Record<string, string>; sort: { key: string; dir: 1 | -1 } | null; hidden: string[] }

const prefsKey = (page: string) => `falcon_table_${page}_v1`;

export function mountTable<T>(container: HTMLElement, o: TableOptions<T>): { refresh(rows: T[]): void; selectedIds(): string[]; shownIds(): string[] } {
  let rows = o.rows;
  const selected = new Set<string>();
  let saved: Array<{ id: string; name: string; state: TableState }> = [];
  const defaults: TableState = {
    search: o.initialSearch || "",
    filters: Object.fromEntries((o.filters || []).map((f) => [f.key, f.initial ?? ""])),
    sort: null,
    hidden: o.columns.filter((c) => c.hidden).map((c) => c.key),
  };
  let st: TableState = structuredClone(defaults);
  try {
    const p = JSON.parse(localStorage.getItem(prefsKey(o.page)) || "null");
    if (p && Array.isArray(p.hidden)) st.hidden = p.hidden.filter((k: string) => o.columns.some((c) => c.key === k));
  } catch { /* private window */ }
  const savePrefs = () => { try { localStorage.setItem(prefsKey(o.page), JSON.stringify({ hidden: st.hidden })); } catch { /* ignore */ } };

  const visibleCols = () => o.columns.filter((c) => !st.hidden.includes(c.key));
  const text = (v: unknown) => (Array.isArray(v) ? v.join(", ") : v === null || v === undefined ? "" : String(v));

  function filtered(): T[] {
    const q = st.search.trim().toLowerCase();
    let out = rows.filter((r) => (o.filters || []).every((f) => (f.toggle ? f.test(r, st.filters[f.key] || "") : !st.filters[f.key] || f.test(r, st.filters[f.key]))));
    if (q) out = out.filter((r) => o.columns.some((c) => text(c.value(r)).toLowerCase().includes(q)));
    if (st.sort) {
      const col = o.columns.find((c) => c.key === st.sort!.key);
      if (col) {
        const dir = st.sort.dir;
        out = [...out].sort((a, b) => {
          const x = col.value(a), y = col.value(b);
          if (typeof x === "number" || typeof y === "number") return ((Number(x ?? -Infinity) || 0) - (Number(y ?? -Infinity) || 0)) * dir;
          return text(x).localeCompare(text(y), "en", { numeric: true }) * dir;
        });
      }
    }
    return out;
  }

  // The drop-down filters and tick boxes sit together in one Filters panel (the same panel as the Time & Action Plan's),
  // so the bar keeps one line: search, Filters, saved filters, columns and downloads.
  const changedFilters = () => (o.filters || []).filter((f) => (st.filters[f.key] || "") !== (defaults.filters[f.key] || "")).length;
  function filtersHtml(): string {
    const list = o.filters || [];
    if (!list.length) return "";
    const n = changedFilters();
    return `<span class="cp-filter-wrap std-filter-wrap">
      <button type="button" class="secondary small-btn cp-filter-btn" data-t="filters" aria-haspopup="dialog" aria-expanded="false"><span class="cp-funnel" aria-hidden="true"></span>Filters<span class="cp-filter-n" data-t="filters-n" ${n ? "" : "hidden"}>${n}</span><span class="cp-caret" aria-hidden="true"></span></button>
      <span class="cp-filter-shade" data-t="filters-close" hidden></span>
      <div class="cp-filter-pop std-filter-pop" data-t="filters-pop" role="dialog" aria-label="Filters" hidden>
        <div class="cp-fp-head"><h4>Filters</h4><button type="button" class="link-btn" data-t="filters-reset">Reset to standard</button></div>
        <div class="cp-fp-sec std-fp-fields">${list.filter((f) => !f.toggle).map((f) => `<label class="std-fp-field"><span>${esc(f.label)}</span><select data-t="filter" data-key="${esc(f.key)}" aria-label="${esc(f.label)}"><option value="">All</option>${f.options.map(([v, l]) => `<option value="${esc(v)}" ${st.filters[f.key] === v ? "selected" : ""}>${esc(l)}</option>`).join("")}</select></label>`).join("")}
        ${list.filter((f) => f.toggle).map((f) => `<label class="check-row cp-fp-row falcon-table-toggle"><input type="checkbox" data-t="toggle" data-key="${esc(f.key)}" ${st.filters[f.key] ? "checked" : ""}><span>${esc(f.label)}</span></label>`).join("")}</div>
        <div class="cp-fp-foot"><span class="cp-fp-hint" data-t="filters-shown"></span><button type="button" class="primary small-btn" data-t="filters-close">Done</button></div>
      </div></span>`;
  }
  function setFiltersOpen(open: boolean) {
    container.querySelectorAll<HTMLElement>('[data-t="filters-pop"], .cp-filter-shade').forEach((el) => { el.hidden = !open; });
    const b = container.querySelector<HTMLElement>('[data-t="filters"]');
    b?.classList.toggle("is-open", open);
    b?.setAttribute("aria-expanded", String(open));
  }

  function renderShellHtml() {
    container.innerHTML = `
<div class="panel falcon-table-panel">
  ${o.title ? `<div class="panel-head"><div><h3>${esc(o.title)}</h3>${o.subtitle ? `<span class="muted">${esc(o.subtitle)}</span>` : ""}</div><div class="toolbar">${o.headActions || ""}</div></div>` : ""}
  <div class="master-filterbar falcon-table-toolbar">
    <input type="search" data-t="search" placeholder="${esc(o.searchPlaceholder || "Search…")}" value="${esc(st.search)}" aria-label="Search">
    ${filtersHtml()}
    <div class="falcon-table-tools">
      <select data-t="view" aria-label="Saved filters"><option value="">Saved filters…</option></select>
      <button type="button" class="secondary small-btn" data-t="save-view" title="Save the current search, filters and sort">Save filter</button>
      <button type="button" class="secondary small-btn" data-t="columns" aria-haspopup="true">Columns</button>
      ${o.exportUrl ? `<button type="button" class="secondary small-btn" data-t="export" data-fmt="xlsx" title="Download the rows shown (after search and filters) as an Excel file">Excel</button><button type="button" class="secondary small-btn" data-t="export" data-fmt="csv" title="Download the rows shown as a CSV file">CSV</button>` : ""}
      ${(o.exportExtra || []).map((x, i) => `<button type="button" class="secondary small-btn" data-t="export-extra" data-i="${i}">${esc(x.label)}</button>`).join("")}
    </div>
    <div class="falcon-columns-pop" data-t="columns-pop" hidden></div>
  </div>
  <div class="falcon-bulk-bar" data-t="bulk" hidden></div>
  ${o.summary ? '<div class="falcon-table-summary" data-t="summary" role="status" aria-live="polite"></div>' : ""}
  <div class="table-wrap falcon-table-wrap"><table class="falcon-table"><thead></thead><tbody></tbody>${o.totals ? '<tfoot class="falcon-table-totals"></tfoot>' : ""}</table></div>
  <div class="falcon-table-foot" data-t="foot"></div>
</div>`;
  }

  function renderBody() {
    const list = filtered();
    const cols = visibleCols();
    const allIds = list.map(o.rowId);
    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
    const selectable = !!(o.bulkActions?.length || o.exportUrl);
    const thead = container.querySelector("thead")!;
    thead.innerHTML = `<tr>${selectable ? `<th class="falcon-check-col"><input type="checkbox" data-t="all" ${allSelected ? "checked" : ""} aria-label="Select all"></th>` : ""}${cols.map((c) => {
      const sorted = st.sort?.key === c.key ? (st.sort.dir === 1 ? " ▲" : " ▼") : "";
      return `<th class="${c.numeric ? "num" : ""}"><button type="button" class="falcon-sort" data-t="sort" data-key="${esc(c.key)}">${esc(c.label)}${sorted}</button></th>`;
    }).join("")}</tr>`;
    const tbody = container.querySelector("tbody")!;
    tbody.innerHTML = list.map((r) => {
      const id = o.rowId(r);
      return `<tr data-id="${esc(id)}" class="${o.rowHref ? "falcon-row-link" : ""} ${o.rowClass?.(r) || ""} ${selected.has(id) ? "selected" : ""}">${selectable ? `<td class="falcon-check-col std-card-check"><input type="checkbox" data-t="row" value="${esc(id)}" ${selected.has(id) ? "checked" : ""} aria-label="Select"></td>` : ""}${cols.map((c, i) => `<td class="${c.className || ""} ${c.numeric ? "num" : ""}${i === 0 ? " std-card-title" : c.key === "status" ? " std-card-status" : ""}" data-label="${esc(c.label)}">${c.html ? c.html(r) : esc(text(c.value(r)))}</td>`).join("")}</tr>`;
    }).join("") || `<tr><td colspan="${cols.length + (selectable ? 1 : 0)}"><div class="empty">${esc(rows.length ? "Nothing matches these filters." : o.emptyText || "Nothing here yet.")}</div></td></tr>`;
    if (o.summary) container.querySelector<HTMLElement>('[data-t="summary"]')!.innerHTML = o.summary(list);
    if (o.totals) {
      const cells = list.length ? o.totals(list) : null;
      container.querySelector("tfoot")!.innerHTML = cells ? `<tr>${selectable ? "<td></td>" : ""}${cols.map((c, i) => `<td class="${c.numeric ? "num" : ""}">${cells[c.key] ?? (i === 0 ? "<strong>Total</strong>" : "")}</td>`).join("")}</tr>` : "";
    }
    const n = changedFilters(), badge = container.querySelector<HTMLElement>('[data-t="filters-n"]');
    if (badge) { badge.hidden = !n; badge.textContent = String(n); }
    const shownHint = container.querySelector<HTMLElement>('[data-t="filters-shown"]');
    if (shownHint) shownHint.textContent = `${list.length} of ${rows.length} shown`;
    container.querySelector<HTMLElement>('[data-t="foot"]')!.textContent = `${list.length} of ${rows.length} shown${selected.size ? ` · ${selected.size} selected` : ""}`;
    const bulk = container.querySelector<HTMLElement>('[data-t="bulk"]')!;
    bulk.hidden = !selected.size;
    bulk.innerHTML = selected.size ? `<strong>${selected.size} selected</strong>${(o.bulkActions || []).map((b) => `<button type="button" class="${b.danger ? "danger" : "secondary"} small-btn" data-t="bulk" data-id="${esc(b.id)}">${esc(b.label)}</button>`).join("")}${o.exportUrl ? `<button type="button" class="secondary small-btn" data-t="export-selected">Export selected</button>` : ""}<button type="button" class="link-btn" data-t="clear">Clear selection</button>` : "";
  }

  async function loadViews() {
    try {
      saved = await get(`/api/org/views?page=${encodeURIComponent(o.page)}`);
    } catch {
      saved = [];
    }
    const sel = container.querySelector<HTMLSelectElement>('[data-t="view"]');
    if (sel) sel.innerHTML = `<option value="">Saved filters…</option>${saved.map((v) => `<option value="${esc(v.id)}">${esc(v.name)}</option>`).join("")}${saved.length ? `<option value="__delete">Delete a saved filter…</option>` : ""}`;
  }

  function applyState(next: TableState) {
    st = { ...structuredClone(defaults), ...next, hidden: st.hidden };
    renderShellHtml();
    renderBody();
    loadViews();
  }

  // ---- events (attached to this table only) ------------------------------------------------------
  container.addEventListener("input", (e) => {
    const t = e.target as HTMLElement;
    if (t.dataset.t === "search") { st.search = (t as HTMLInputElement).value; renderBody(); }
  });
  container.addEventListener("change", async (e) => {
    const t = e.target as HTMLInputElement;
    switch (t.dataset.t) {
      case "filter": st.filters[t.dataset.key!] = t.value; renderBody(); break;
      case "toggle": st.filters[t.dataset.key!] = t.checked ? "1" : ""; renderBody(); break;
      case "row": if (t.checked) selected.add(t.value); else selected.delete(t.value); renderBody(); break;
      case "all": for (const id of filtered().map(o.rowId)) { if (t.checked) selected.add(id); else selected.delete(id); } renderBody(); break;
      case "col": {
        const key = t.value;
        st.hidden = t.checked ? st.hidden.filter((k) => k !== key) : [...st.hidden, key];
        savePrefs();
        renderBody();
        break;
      }
      case "view": {
        const v = (t as unknown as HTMLSelectElement).value;
        if (v === "__delete") {
          const pick = await chooseValue("Which saved filter should be deleted?", saved.map((x) => [x.id, x.name]), "Delete");
          const found = saved.find((x) => x.id === pick);
          if (found) { await del(`/api/org/views/${found.id}`); toast("Saved filter deleted."); }
          await loadViews();
          return;
        }
        const found = saved.find((x) => x.id === v);
        if (found) applyState(found.state);
        break;
      }
      case "bulk-value": break;
    }
  });
  container.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-t]");
    if (!t) {
      const tr = (e.target as HTMLElement).closest<HTMLTableRowElement>("tr[data-id]");
      if (tr && o.rowHref && !(e.target as HTMLElement).closest("a,button,input,select,label")) {
        const r = rows.find((x) => o.rowId(x) === tr.dataset.id);
        if (r) navigate(o.rowHref(r));
      }
      return;
    }
    switch (t.dataset.t) {
      case "filters": setFiltersOpen(t.getAttribute("aria-expanded") !== "true"); break;
      case "filters-close": setFiltersOpen(false); break;
      case "filters-reset": {
        st.filters = structuredClone(defaults.filters);
        container.querySelectorAll<HTMLSelectElement>('select[data-t="filter"]').forEach((el) => { el.value = st.filters[el.dataset.key!] || ""; });
        container.querySelectorAll<HTMLInputElement>('input[data-t="toggle"]').forEach((el) => { el.checked = !!st.filters[el.dataset.key!]; });
        renderBody();
        break;
      }
      case "sort": {
        const key = t.dataset.key!;
        st.sort = st.sort?.key === key ? (st.sort.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 };
        renderBody();
        break;
      }
      case "columns": {
        const pop = container.querySelector<HTMLElement>('[data-t="columns-pop"]')!;
        pop.hidden = !pop.hidden;
        pop.innerHTML = `<strong>Show columns</strong>${o.columns.map((c) => `<label class="check-row"><input type="checkbox" data-t="col" value="${esc(c.key)}" ${st.hidden.includes(c.key) ? "" : "checked"}><span>${esc(c.label)}</span></label>`).join("")}`;
        break;
      }
      case "save-view": {
        const name = await askText("Name this filter", "For example: Active coveralls");
        if (!name?.trim()) return;
        try {
          await post("/api/org/views", { page: o.page, name: name.trim(), state: { search: st.search, filters: st.filters, sort: st.sort } });
          toast("Filter saved. Choose it from Saved filters any time.");
          await loadViews();
        } catch (err) { toast((err as Error).message, "error"); }
        break;
      }
      case "export": case "export-extra": {
        // Everything, or only the rows the search and filters leave on screen.
        const shown = filtered().map(o.rowId);
        const ids = shown.length === rows.length ? null : shown;
        if (ids && !ids.length) { toast("No rows are shown, so there is nothing to export.", "error"); break; }
        window.location.href = t.dataset.t === "export" ? o.exportUrl!(ids, (t.dataset.fmt as "xlsx" | "csv") || "xlsx") : o.exportExtra![Number(t.dataset.i)].url(ids);
        break;
      }
      case "export-selected": window.location.href = o.exportUrl!([...selected], "xlsx"); break;
      case "clear": selected.clear(); renderBody(); break;
      case "bulk": {
        const action = (o.bulkActions || []).find((b) => b.id === t.dataset.id);
        if (!action || !o.onBulk) return;
        let value: string | undefined;
        if (action.choose) {
          value = await chooseValue(action.choose.label, action.choose.options);
          if (value === undefined) return;
        }
        if (action.confirm && !(await confirmBox(action.confirm.replace("{n}", String(selected.size)), action.label, !!action.danger))) return;
        try {
          await o.onBulk(action.id, [...selected], value);
          selected.clear();
        } catch (err) { toast((err as Error).message, "error"); }
        break;
      }
    }
  });
  const closePop = (e: MouseEvent) => {
    if (!container.isConnected) return document.removeEventListener("click", closePop);
    const pop = container.querySelector<HTMLElement>('[data-t="columns-pop"]');
    if (pop && !pop.hidden && !(e.target as HTMLElement).closest('[data-t="columns-pop"],[data-t="columns"]')) pop.hidden = true;
    const fp = container.querySelector<HTMLElement>('[data-t="filters-pop"]');
    if (fp && !fp.hidden && !(e.target as HTMLElement).closest(".std-filter-wrap, #modal, .confirm-box")) setFiltersOpen(false);
  };
  document.addEventListener("click", closePop);
  container.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !container.querySelector<HTMLElement>('[data-t="filters-pop"]')?.hidden) { setFiltersOpen(false); container.querySelector<HTMLElement>('[data-t="filters"]')?.focus(); }
  });

  renderShellHtml();
  renderBody();
  loadViews();
  return {
    refresh(next: T[]) {
      rows = next;
      for (const id of [...selected]) if (!rows.some((r) => o.rowId(r) === id)) selected.delete(id);
      renderBody();
    },
    selectedIds: () => [...selected],
    shownIds: () => filtered().map(o.rowId),
  };
}

/** A small chooser for bulk actions that need a value (category, supplier). */
function chooseValue(label: string, options: Array<[string, string]>, okLabel = "Apply"): Promise<string | undefined> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<div class="confirm-box"><h3>${esc(label)}</h3><select class="falcon-choose">${options.map(([v, l]) => `<option value="${esc(v)}">${esc(l)}</option>`).join("")}</select><div class="confirm-actions"><button type="button" class="secondary" data-v="no">Cancel</button><button type="button" class="primary" data-v="yes">${esc(okLabel)}</button></div></div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest("button");
      if (!b) return;
      const v = overlay.querySelector<HTMLSelectElement>("select")!.value;
      overlay.remove();
      resolve(b.dataset.v === "yes" ? v : undefined);
    });
  });
}

/** A small box asking for one line of text (instead of the browser's own prompt). */
function askText(label: string, placeholder: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "confirm-overlay falcon-confirm";
    overlay.innerHTML = `<form class="confirm-box"><h3>${esc(label)}</h3><input class="falcon-choose" maxlength="60" placeholder="${esc(placeholder)}" required><div class="confirm-actions"><button type="button" class="secondary" data-v="no">Cancel</button><button type="submit" class="primary">Save</button></div></form>`;
    document.body.appendChild(overlay);
    const input = overlay.querySelector("input")!;
    const done = (v: string | undefined) => { overlay.remove(); resolve(v); };
    overlay.querySelector("form")!.addEventListener("submit", (e) => { e.preventDefault(); e.stopPropagation(); done(input.value.trim() || undefined); });
    overlay.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest('[data-v="no"]') || e.target === overlay) done(undefined); });
    overlay.addEventListener("keydown", (e) => { if (e.key === "Escape") { e.stopPropagation(); done(undefined); } });
    input.focus();
  });
}
